import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Link from 'next/link';
import Button from '@components/Shared/Buttons/Button';
import AspectImage from '@components/Shared/AspectImage/AspectImage';
import useWindowResize from 'pubweb-smokey/dist/hooks/useWindowResize';
import * as variables from 'pubweb-smokey/dist/components/GridSystem/_vars_widths.js';
import Colors from 'pubweb-smokey/dist/colors';
import { extractColors, formatHttpsUrl } from '@utils/utils';
import { convertButtonColor, reverseColors } from '@utils/ctaUtils';
import HeroStyles, { imageAspectRatios } from './Hero.styled';

const Hero = ({ heroHeadline, heroData }) => {
  const heroImages = {
    desktop: formatHttpsUrl(
      `${heroData.heroImage[0]?.media?.file?.url}?w=700&fm=webp`
    ),
    mobile: formatHttpsUrl(
      `${heroData.mobileHeroImage[0]?.media?.file?.url}?w=660&fm=webp`
    ),
  };

  const [altText, setAltText] = useState('');

  useWindowResize(() => {
    if (window.innerWidth >= variables.desktop_breakpoint_extended) {
      setAltText(heroData.heroImage[0].altText);
    } else {
      setAltText(heroData.mobileHeroImage[0].altText);
    }
  }, [variables.desktop_breakpoint_extended]);

  const buttonColors = extractColors(heroData?.heroCTA?.buttonAndTextColors);
  const backgroundColor = extractColors(heroData?.heroCTA?.ctaBackgroundColor);

  const buttonOnDarkBg = buttonColors && reverseColors(buttonColors);
  const btnColor = buttonColors && convertButtonColor(buttonColors);
  const outlineButton =
    btnColor &&
    ((backgroundColor &&
      backgroundColor[0].toLowerCase() === btnColor.standard) ||
      btnColor === Colors.secondary.transparent ||
      buttonColors[0].toLowerCase() === '#ffffff');
  const borderColor =
    btnColor && btnColor.borderColor
      ? btnColor.borderColor
      : btnColor && btnColor.textColor
        ? btnColor.textColor
        : buttonOnDarkBg
          ? btnColor.standard
          : Colors.primary.white.standard;

  return (
    <HeroStyles borderColor={borderColor}>
      {heroHeadline && <h1>{heroHeadline}</h1>}
      <div className="hero-container">
        <div className="hero-bottom-cover"></div>
        <div className="hero-content">
          <div className="hero-image">
            <AspectImage
              behavior="fill-container"
              alt={altText}
              containerOrientation="landscape"
              srcset={[
                {
                  src: heroImages.desktop,
                  mediaWidth: `${variables.desktop_breakpoint_extended}`,
                  aspectRatio: imageAspectRatios.desktop,
                  imgOrientation: 'landscape',
                },
                {
                  src: heroImages.mobile,
                  aspectRatio: imageAspectRatios.mobile,
                  imgOrientation: 'landscape',
                },
              ]}
            />
          </div>
          <div className="hero-cta">
            <div className="hero-cta-tags">
              <span className="hero-cta-cateogry label">
                {heroData.heroCTA.ctaCustomCategory} |{' '}
              </span>
              <span className="hero-cta-tag label">
                {heroData.heroCTA.ctaTag}
              </span>
            </div>
            <span className="hero-cta-header h1">
              {heroData.heroCTA.ctaHeader}
            </span>
            <Button
              className={`gtm-studio-hero-cta-button hero-cta-button ${
                outlineButton ? 'cta-button-outline' : ''
              }`}
              fillContainer={false}
              nextJSUrl={heroData.heroCTA.firstCtaButtonUrl}
              linkComponent={Link}
              primaryColor={btnColor}
              buttonStyle={
                btnColor === Colors.primary.white ? 'outlined' : null
              }
              onDarkBg={buttonOnDarkBg}
              automationId="hero-cta-button"
            >
              {heroData.heroCTA.firstCtaButtonText}
            </Button>
          </div>
        </div>
      </div>
    </HeroStyles>
  );
};

Hero.propTypes = {
  heroCTA: PropTypes.shape({
    buttonAndTextColors: PropTypes.any,
    ctaBackgroundColor: PropTypes.any,
    ctaCustomCategory: PropTypes.any,
    ctaHeader: PropTypes.any,
    ctaTag: PropTypes.any,
    firstCtaButtonText: PropTypes.any,
    firstCtaButtonUrl: PropTypes.any,
  }),
  heroData: PropTypes.shape({
    heroCTA: PropTypes.shape({
      buttonAndTextColors: PropTypes.any,
      ctaBackgroundColor: PropTypes.any,
      ctaCustomCategory: PropTypes.any,
      ctaHeader: PropTypes.any,
      ctaTag: PropTypes.any,
      firstCtaButtonText: PropTypes.any,
      firstCtaButtonUrl: PropTypes.any,
    }),
    heroImage: PropTypes.any,
    mobileHeroImage: PropTypes.any,
  }),
  heroHeadline: PropTypes.string,
};

export default Hero;
